import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Search, Phone, Mail, MapPin, Edit, Trash2, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCallCustomer } from '../../hooks/useCallCustomer';

const PharmacyCRM = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const { callCustomer, isCalling } = useCallCustomer();
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers?firm=pharmacy`, { 
        headers: getAuthHeaders() 
      });
      setCustomers(response.data);
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch customers');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, {
          firm: 'pharmacy',
          ...newCustomer
        }, { headers: getAuthHeaders() });
        toast.success('Customer updated');
      } else {
        await axios.post(`${API}/customers`, {
          firm: 'pharmacy',
          ...newCustomer
        }, { headers: getAuthHeaders() });
        toast.success('Customer added');
      }
      
      setShowAdd(false);
      setEditingCustomer(null);
      setNewCustomer({ name: '', phone: '', email: '', address: '', notes: '' });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setNewCustomer({
      name: customer.name,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || '',
      notes: customer.notes || ''
    });
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`, { headers: getAuthHeaders() });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const sendWhatsApp = (customer) => {
    const phone = customer.phone.replace(/[^0-9]/g, '');
    const message = `Hello ${customer.name},\n\nThank you for being our valued customer!\n\nShri Kripa Pharmacy`;
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone.includes(searchQuery) ||
    (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) return <Layout><div className="flex items-center justify-center h-96"><div className="text-slate-500">Loading...</div></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading tracking-tight text-slate-900 mb-2">CRM - Customers</h1>
            <p className="text-slate-500">Manage your pharmacy customers</p>
          </div>
          <Dialog open={showAdd} onOpenChange={(open) => {
            setShowAdd(open);
            if (!open) {
              setEditingCustomer(null);
              setNewCustomer({ name: '', phone: '', email: '', address: '', notes: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-2" />Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading tracking-tight">
                  {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} required className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} required className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} rows={2} className="border-slate-200" />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={newCustomer.notes} onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })} rows={3} className="border-slate-200" placeholder="Any additional information..." />
                </div>
                <Button type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white">
                  {editingCustomer ? 'Update Customer' : 'Add Customer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search customers by name, phone, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-slate-200"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Total Customers</p>
              <p className="text-3xl font-heading tracking-tight text-slate-900">{customers.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Customers List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.length === 0 ? (
            <Card className="col-span-full p-12 text-center border-slate-200">
              <p className="text-slate-600 mb-4">No customers found</p>
              <Button onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-2" />Add Your First Customer
              </Button>
            </Card>
          ) : (
            filteredCustomers.map((customer) => (
              <Card key={customer.id} className="border-slate-200 hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-heading tracking-tight">{customer.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => handleEdit(customer)} className="h-8 w-8">
                        <Edit className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(customer.id)} className="h-8 w-8">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-4 w-4" />
                    <span>{customer.phone}</span>
                  </div>
                  {customer.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.address && (
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="h-4 w-4 mt-0.5" />
                      <span className="line-clamp-2">{customer.address}</span>
                    </div>
                  )}
                  {customer.notes && (
                    <div className="mt-3 p-3 bg-slate-50 rounded text-sm text-slate-600">
                      <p className="line-clamp-2">{customer.notes}</p>
                    </div>
                  )}
                  <Button 
                    onClick={() => sendWhatsApp(customer)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white mt-3"
                    size="sm"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />Send WhatsApp
                  </Button>
                  <Button 
                    onClick={() => callCustomer(customer.phone, customer.name)}
                    disabled={isCalling}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-2"
                    size="sm"
                    data-testid={`call-now-${customer.id}`}
                  >
                    {isCalling ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Phone className="h-4 w-4 mr-2" />
                    )}
                    Call Now
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PharmacyCRM;
